import React, { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LocationInput } from '@/components/LocationInput';
import { splitDomicile, composeDomicile } from '@/data/locations';
import { WhatsAppInput } from '@/components/WhatsAppInput';
import { CATEGORY_NAMES } from '@/data/categories';
import {
  SHIO_OPTIONS, MBTI_OPTIONS, ZODIAC_OPTIONS, MARRIAGE_STATUS_OPTIONS,
  TUJUAN_OPTIONS, INCOME_RANGE_OPTIONS, ALIRAN_AGAMA_OPTIONS,
} from '@/data/profileFields';
import { ExtendedProfileEditor } from '@/components/ExtendedProfileEditor';

const inputCls = "t-bg-input t-border h-10 rounded-lg text-sm px-4 t-text";

/**
 * Shared form-fields block used by both the Create and Edit Member admin dialogs.
 * The parent owns the form state — this component is purely controlled.
 *
 * Props:
 *   form: form state object
 *   setForm: setter for the form state
 *   mode: 'create' | 'edit'  (controls visibility of the Name field in edit-only)
 *   lang: 'en' | 'id'
 *   t: translation function
 */
export const MemberFormFields = ({ form, setForm, mode, lang, t }) => {
  const aliranOptionsFlat = useMemo(
    () => Object.entries(ALIRAN_AGAMA_OPTIONS).flatMap(([r, sects]) => sects.map(s => `${r} - ${s}`)),
    []
  );

  const update = (patch) => setForm({ ...form, ...patch });
  const igHandler = (v) => v.replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\/@?/i, '').split('?')[0].split('/')[0];
  const ttHandler = (v) => v.replace(/^@/, '').replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, '').split('?')[0].split('/')[0];

  // Location split: prefer explicit country/city/location_detail, else parse legacy domicile.
  const location = useMemo(() => {
    if (form.country || form.city || form.location_detail) {
      return { country: form.country || '', city: form.city || '', location_detail: form.location_detail || '' };
    }
    return splitDomicile(form.domicile || '');
  }, [form.country, form.city, form.location_detail, form.domicile]);

  const updateLocation = (patch) => {
    const next = { ...location, ...patch };
    setForm({
      ...form,
      country: next.country,
      city: next.city,
      location_detail: next.location_detail,
      domicile: composeDomicile(next),
    });
  };

  return (
    <>
      {mode === 'edit' && (
        <Input data-testid="dialog-edit-name" value={form.name || ''} onChange={e => update({ name: e.target.value })} className={inputCls} placeholder={t('name')} />
      )}

      <div className="grid grid-cols-2 gap-3">
        {mode === 'edit' ? (
          <>
            <Input data-testid="dialog-edit-age" type="number" value={form.age || ''} onChange={e => update({ age: e.target.value })} className={inputCls} placeholder={t('age')} />
            <Select value={form.gender || ''} onValueChange={v => update({ gender: v })}>
              <SelectTrigger data-testid="dialog-edit-gender" className="t-bg-input t-border h-10 rounded-lg text-sm t-text"><SelectValue placeholder={t('gender')} /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="male" className="t-text">{t('male')}</SelectItem>
                <SelectItem value="female" className="t-text">{t('female')}</SelectItem>
              </SelectContent>
            </Select>
          </>
        ) : (
          <>
            <Select value={form.gender || ''} onValueChange={v => update({ gender: v })}>
              <SelectTrigger data-testid="create-gender" className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={t('gender')} /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="male" className="t-text">{t('male')}</SelectItem>
                <SelectItem value="female" className="t-text">{t('female')}</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="create-age" type="number" value={form.age || ''} onChange={e => update({ age: e.target.value })} className={inputCls} placeholder={t('age')} />
          </>
        )}
      </div>

      <Select value={form.category || ''} onValueChange={v => update({ category: v })}>
        <SelectTrigger data-testid={`${mode}-category`} className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={t('category')} /></SelectTrigger>
        <SelectContent className="glass-card t-text">
          {CATEGORY_NAMES.map(c => <SelectItem key={c} value={c} className="t-text">{c}</SelectItem>)}
        </SelectContent>
      </Select>

      <LocationInput value={location} onChange={updateLocation} lang={lang} testIdPrefix={`${mode}-location`} />

      {mode === 'create' && (
        <Select value={form.education || ''} onValueChange={v => update({ education: v })}>
          <SelectTrigger data-testid="create-education" className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={t('education')} /></SelectTrigger>
          <SelectContent className="glass-card t-text">
            {['SMA/SMK', 'D3', 'S1', 'S2', 'S3'].map(e => <SelectItem key={e} value={e} className="t-text">{e}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <Input data-testid={`${mode}-occupation`} value={form.occupation || ''} onChange={e => update({ occupation: e.target.value })} className={inputCls} placeholder={t('occupation')} />
      <WhatsAppInput value={form.whatsapp || ''} onChange={v => update({ whatsapp: v })} lang={lang} />
      <Input data-testid={`${mode}-instagram`} value={form.instagram || ''} onChange={e => update({ instagram: igHandler(e.target.value) })} className={inputCls} placeholder="Instagram username (e.g. johndoe)" />
      <Input data-testid={`${mode}-tiktok`} value={form.tiktok || ''} onChange={e => update({ tiktok: ttHandler(e.target.value) })} className={inputCls} placeholder="TikTok username (e.g. johndoe)" />
      <Input data-testid={`${mode}-facebook`} value={form.facebook || ''} onChange={e => update({ facebook: e.target.value })} className={inputCls} placeholder="Facebook full URL (e.g. https://facebook.com/...)" />
      <Textarea data-testid={`${mode}-bio`} value={form.bio || ''} onChange={e => update({ bio: e.target.value })} className="t-bg-input t-border rounded-lg text-sm px-4 py-3 t-text" placeholder={t('bio')} />

      <div className="pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <p className="t-text-muted text-xs uppercase tracking-wider mb-3">{lang === 'en' ? (mode === 'create' ? 'Extended Info (Optional)' : 'Extended Info') : (mode === 'create' ? 'Info Tambahan (Opsional)' : 'Info Tambahan')}</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.marriage_status || 'none'} onValueChange={v => update({ marriage_status: v === 'none' ? '' : v })}>
              <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={lang === 'en' ? 'Marriage Status' : 'Status'} /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="none" className="t-text">-</SelectItem>
                {MARRIAGE_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="t-text">{lang === 'en' ? o.en : o.id}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.tujuan_join || 'none'} onValueChange={v => update({ tujuan_join: v === 'none' ? '' : v })}>
              <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={lang === 'en' ? 'Purpose' : 'Tujuan'} /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="none" className="t-text">-</SelectItem>
                {TUJUAN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="t-text">{lang === 'en' ? o.en : o.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Select value={form.mbti || 'none'} onValueChange={v => update({ mbti: v === 'none' ? '' : v })}>
              <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder="MBTI" /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="none" className="t-text">-</SelectItem>
                {MBTI_OPTIONS.map(m => <SelectItem key={m} value={m} className="t-text">{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.zodiac || 'none'} onValueChange={v => update({ zodiac: v === 'none' ? '' : v })}>
              <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder="Zodiac" /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="none" className="t-text">-</SelectItem>
                {ZODIAC_OPTIONS.map(z => <SelectItem key={z} value={z} className="t-text">{z}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.shio || 'none'} onValueChange={v => update({ shio: v === 'none' ? '' : v })}>
              <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder="Shio" /></SelectTrigger>
              <SelectContent className="glass-card t-text">
                <SelectItem value="none" className="t-text">-</SelectItem>
                {SHIO_OPTIONS.map(s => <SelectItem key={s} value={s} className="t-text">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Select value={form.aliran_agama || 'none'} onValueChange={v => update({ aliran_agama: v === 'none' ? '' : v })}>
            <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={lang === 'en' ? 'Religious Sect' : 'Aliran Agama'} /></SelectTrigger>
            <SelectContent className="glass-card t-text max-h-[200px]">
              <SelectItem value="none" className="t-text">-</SelectItem>
              {aliranOptionsFlat.map(a => <SelectItem key={a} value={a} className="t-text">{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.income_range || 'none'} onValueChange={v => update({ income_range: v === 'none' ? '' : v })}>
            <SelectTrigger className="t-bg-input t-border h-10 rounded-lg t-text text-sm"><SelectValue placeholder={lang === 'en' ? 'Monthly Income (IDR)' : 'Pendapatan Bulanan'} /></SelectTrigger>
            <SelectContent className="glass-card t-text">
              <SelectItem value="none" className="t-text">-</SelectItem>
              {INCOME_RANGE_OPTIONS.map(r => <SelectItem key={r} value={r} className="t-text">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={form.hobby || ''} onChange={e => update({ hobby: e.target.value })} className={inputCls} placeholder="Hobby" />
          <Input value={form.preferred_places || ''} onChange={e => update({ preferred_places: e.target.value })} className={inputCls} placeholder={lang === 'en' ? 'Preferred Places' : 'Tempat Favorit'} />
          <Input value={form.topics_to_talk || ''} onChange={e => update({ topics_to_talk: e.target.value })} className={inputCls} placeholder={lang === 'en' ? 'Topics to Talk' : 'Topik Pembicaraan'} />
          <Textarea value={form.kriteria_pasangan || ''} onChange={e => update({ kriteria_pasangan: e.target.value })} className="t-bg-input t-border rounded-lg text-sm px-4 py-3 t-text" placeholder={lang === 'en' ? 'Partner Criteria' : 'Kriteria Pasangan'} />
        </div>
      </div>

      {/* ── Extended optional profile (Batch 3) ────────────────── */}
      <div>
        <h4 className="text-sm font-semibold t-text mb-2">
          {lang === 'en' ? 'Extended Profile (optional)' : 'Informasi Tambahan (Opsional)'}
        </h4>
        <ExtendedProfileEditor
          value={form}
          onFieldChange={(k, v) => update({ [k]: v })}
          lang={lang}
        />
      </div>
    </>
  );
};
